import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, Row, Col, Card, Form, Button, Alert, Spinner, 
  Accordion, Badge, ButtonGroup, Modal 
} from 'react-bootstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { 
  getAdvancedCriteriaSettings,
  toggleAdvancedCriteria,
  getAdvancedCriteriaFields,
  syncLendersToAdvancedCriteria,
  fixLenderIds,
  updateAllLenderCriteria,
  copyCriteria,
  createConditionGroup,
  createCondition,
  createCriterion,
  createConditionalRule
} from '../api/advancedCriteriaApi';
import { getIndustryPicklistValues } from '../api/salesforceApi';
import { FaPlus, FaTrash, FaCopy, FaSync, FaCodeBranch } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import '../styles/LenderAdvancedCriteria.css';

// List of US state codes
const stateCodes = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'AS', 'GU', 'MP', 'PR', 'VI'
];

/**
 * LenderAdvancedCriteria component for configuring conditional lender criteria
 * 
 * This component allows administrators to:
 * - Enable/disable the advanced criteria system
 * - Configure simple (non-conditional) criteria
 * - Configure conditional IF-THEN-ELSE rules with AND/OR logic
 * - Support nested conditions and rules
 */
const LenderAdvancedCriteria = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success, error: notifyError } = useNotifications();
  
  // State variables
  const [settings, setSettings] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lenders, setLenders] = useState([]);
  const [criteriaChanged, setCriteriaChanged] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  
  // State for dynamic industry picklist values
  const [dynamicIndustries, setDynamicIndustries] = useState([]);
  
  // State for copy modal
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [copyTarget, setCopyTarget] = useState(null);
  
  // List of numeric text fields
  const numericTextFields = [
    'AverageBankBalance',
    'AnnualRevenue',
    'AverageMonthlySales',
    'AverageNSFs',
    'AverageNumberOfDeposits',
    'AverageNumberOfNegDays',
    'RequestedAmount',
    'ContactOwnershipPercentage',
    'CumulativeOwnershipPercentage',
    'FederalTaxID',
    'NumberOfPositions',
    'PayoffAmountNeeded',
    'ContactCreditScore',
  ];
  
  // Fetch industries on mount
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const industries = await getIndustryPicklistValues();
        setDynamicIndustries(industries);
      } catch (err) {
        console.error('Error fetching industries:', err);
        setDynamicIndustries([]);
      }
    };
    fetchIndustries();
  }, []);
  
  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.role !== 'developer') {
      navigate('/');
    }
  }, [user, navigate]);
  
  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch advanced criteria settings
        const settingsData = await getAdvancedCriteriaSettings();
        setSettings(settingsData);
        setEnabled(settingsData.enabled);
        
        // Process lenders
        if (settingsData.lenders && settingsData.lenders.length > 0) {
          const processedLenders = settingsData.lenders.map(lender => ({
            id: lender.lenderId,
            name: lender.name,
            financingType: lender.financingType || 'working-capital',
            hideUntilQualified: lender.hideUntilQualified === true,
            simpleCriteria: lender.simpleCriteria || [],
            conditionalRules: lender.conditionalRules || []
          }));
          setLenders(processedLenders);
        }
        
        // Fetch available fields
        const fieldsData = await getAdvancedCriteriaFields();
        setAvailableFields(fieldsData);
        
      } catch (err) {
        console.error('Error fetching advanced criteria settings:', err);
        setError('Failed to load advanced criteria settings. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Toggle enabled status
  const handleToggleChange = async (e) => {
    const newEnabled = e.target.checked;
    
    try {
      setSaving(true);
      await toggleAdvancedCriteria(newEnabled);
      setEnabled(newEnabled);
      success(`Advanced criteria ${newEnabled ? 'enabled' : 'disabled'} successfully`);
    } catch (err) {
      console.error('Error toggling advanced criteria:', err);
      notifyError('Failed to update advanced criteria status');
    } finally {
      setSaving(false);
    }
  };
  
  // Sync lenders
  const handleSyncLenders = async () => {
    try {
      setSaving(true);
      await syncLendersToAdvancedCriteria();
      
      // Reload settings
      const settingsData = await getAdvancedCriteriaSettings();
      setSettings(settingsData);
      
      if (settingsData.lenders) {
        const processedLenders = settingsData.lenders.map(lender => ({
          id: lender.lenderId,
          name: lender.name,
          financingType: lender.financingType || 'working-capital',
          hideUntilQualified: lender.hideUntilQualified === true,
          simpleCriteria: lender.simpleCriteria || [],
          conditionalRules: lender.conditionalRules || []
        }));
        setLenders(processedLenders);
      }
      
      success('Lenders synced successfully');
    } catch (err) {
      console.error('Error syncing lenders:', err);
      notifyError('Failed to sync lenders');
    } finally {
      setSaving(false);
    }
  };
  
  // Fix lender IDs (one-time migration)
  const handleFixLenderIds = async () => {
    try {
      setSaving(true);
      const result = await fixLenderIds();
      
      // Reload settings
      const settingsData = await getAdvancedCriteriaSettings();
      setSettings(settingsData);
      
      if (settingsData.lenders) {
        const processedLenders = settingsData.lenders.map(lender => ({
          id: lender.lenderId,
          name: lender.name,
          financingType: lender.financingType || 'working-capital',
          hideUntilQualified: lender.hideUntilQualified === true,
          simpleCriteria: lender.simpleCriteria || [],
          conditionalRules: lender.conditionalRules || []
        }));
        setLenders(processedLenders);
      }
      
      success(`Fixed ${result.fixedCount} lender IDs, added ${result.addedCount} new lenders`);
    } catch (err) {
      console.error('Error fixing lender IDs:', err);
      notifyError('Failed to fix lender IDs');
    } finally {
      setSaving(false);
    }
  };
  
  // Helper functions
  const shouldUseNumericOperators = (fieldId, type) => {
    // Currency fields should also use numeric operators
    if (type === 'number' || type === 'currency') return true;
    return numericTextFields.includes(fieldId);
  };
  
  const isDateComparisonField = (fieldId) => {
    return fieldId === 'BusinessStartDate' || fieldId === 'DateOfBirth' || 
           fieldId === 'Contact2DateOfBirth';
  };
  
  const isStateField = (fieldId) => {
    return fieldId === 'BusinessState' || fieldId === 'ContactState' || 
           fieldId === 'Contact2State';
  };
  
  // Get operators for a field
  const getFieldOperators = (fieldId) => {
    const field = availableFields.find(f => f.id === fieldId);
    const type = field?.type?.toLowerCase() || 'string';
    
    if (fieldId === 'Industry') {
      return [
        { value: '=', label: 'Equals' },
        { value: 'is_one_of', label: 'Is One Of' },
        { value: 'is_not', label: 'Is Not One Of' }
      ];
    }
    
    if (isStateField(fieldId)) {
      return [
        { value: '=', label: 'Equals' },
        { value: 'is_one_of', label: 'Is One Of' },
        { value: 'is_not', label: 'Is Not One Of' }
      ];
    }
    
    if (isDateComparisonField(fieldId)) {
      return [
        { value: '>', label: 'After' },
        { value: '>=', label: 'On or after' },
        { value: '=', label: 'On' },
        { value: '<=', label: 'On or before' },
        { value: '<', label: 'Before' },
        { value: 'older_than_months', label: 'Older than (months)' }
      ];
    }
    
    if (shouldUseNumericOperators(fieldId, type)) {
      return [
        { value: '>', label: 'Greater than' },
        { value: '>=', label: 'Greater than or equal to' },
        { value: '=', label: 'Equals' },
        { value: '<=', label: 'Less than or equal to' },
        { value: '<', label: 'Less than' }
      ];
    }
    
    return [
      { value: '=', label: 'Equals' },
      { value: 'contains', label: 'Contains' },
      { value: 'is_one_of', label: 'Is One Of' },
      { value: 'is_not', label: 'Is Not One Of' }
    ];
  };
  
  // Group fields by category
  const fieldsByCategory = availableFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {});
  
  const fieldOptions = Object.entries(fieldsByCategory).map(([category, fields]) => ({
    label: category,
    options: fields.map(field => ({
      value: field.id,
      label: field.name,
      type: field.type
    }))
  }));
  
  // Select styles
  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 })
  };
  
  // ============================================================================
  // SIMPLE CRITERIA HANDLERS
  // ============================================================================
  
  const addSimpleCriterion = (lenderId) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    updatedLenders[lenderIndex].simpleCriteria.push({
      fieldId: '',
      fieldName: '',
      operator: '>=',
      value: '',
      weight: 5,
      required: false
    });
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  const updateSimpleCriterion = (lenderId, criterionIndex, field, value) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    const criterion = updatedLenders[lenderIndex].simpleCriteria[criterionIndex];
    
    if (field === 'fieldId') {
      const selectedField = availableFields.find(f => f.id === value);
      criterion.fieldId = value;
      criterion.fieldName = selectedField?.name || value;
      criterion.operator = '>=';
      criterion.value = '';
    } else {
      criterion[field] = value;
    }
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  const removeSimpleCriterion = (lenderId, criterionIndex) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    updatedLenders[lenderIndex].simpleCriteria.splice(criterionIndex, 1);
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  // ============================================================================
  // CONDITIONAL RULES HANDLERS
  // ============================================================================
  
  const addConditionalRule = (lenderId) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    updatedLenders[lenderIndex].conditionalRules.push({
      id: uuidv4(),
      name: `Rule ${updatedLenders[lenderIndex].conditionalRules.length + 1}`,
      enabled: true,
      weight: 5,
      required: false,
      conditionGroup: { logic: 'AND', conditions: [], groups: [] },
      thenCriteria: [],
      elseCriteria: [],
      nestedRules: []
    });
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  const updateConditionalRule = (lenderId, ruleIndex, updates) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    updatedLenders[lenderIndex].conditionalRules[ruleIndex] = {
      ...updatedLenders[lenderIndex].conditionalRules[ruleIndex],
      ...updates
    };
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  const removeConditionalRule = (lenderId, ruleIndex) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    updatedLenders[lenderIndex].conditionalRules.splice(ruleIndex, 1);
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  // Add condition to a condition group
  const addConditionToGroup = (lenderId, ruleIndex, groupPath = []) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    let conditionGroup = updatedLenders[lenderIndex].conditionalRules[ruleIndex].conditionGroup;
    
    // Navigate to nested group if path provided
    for (const idx of groupPath) {
      conditionGroup = conditionGroup.groups[idx];
    }
    
    conditionGroup.conditions.push({
      fieldId: '',
      fieldName: '',
      operator: '=',
      value: ''
    });
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  // Update condition in a condition group
  const updateCondition = (lenderId, ruleIndex, conditionIndex, field, value, groupPath = []) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    let conditionGroup = updatedLenders[lenderIndex].conditionalRules[ruleIndex].conditionGroup;
    
    // Navigate to nested group if path provided
    for (const idx of groupPath) {
      conditionGroup = conditionGroup.groups[idx];
    }
    
    const condition = conditionGroup.conditions[conditionIndex];
    
    if (field === 'fieldId') {
      const selectedField = availableFields.find(f => f.id === value);
      condition.fieldId = value;
      condition.fieldName = selectedField?.name || value;
      condition.operator = '=';
      condition.value = '';
    } else {
      condition[field] = value;
    }
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  // Remove condition from a condition group
  const removeCondition = (lenderId, ruleIndex, conditionIndex, groupPath = []) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    let conditionGroup = updatedLenders[lenderIndex].conditionalRules[ruleIndex].conditionGroup;
    
    for (const idx of groupPath) {
      conditionGroup = conditionGroup.groups[idx];
    }
    
    conditionGroup.conditions.splice(conditionIndex, 1);
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  // Add nested group
  const addNestedGroup = (lenderId, ruleIndex, groupPath = []) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    let conditionGroup = updatedLenders[lenderIndex].conditionalRules[ruleIndex].conditionGroup;
    
    for (const idx of groupPath) {
      conditionGroup = conditionGroup.groups[idx];
    }
    
    conditionGroup.groups.push({
      logic: 'OR',
      conditions: [],
      groups: []
    });
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  // Toggle group logic (AND/OR)
  const toggleGroupLogic = (lenderId, ruleIndex, groupPath = []) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    let conditionGroup = updatedLenders[lenderIndex].conditionalRules[ruleIndex].conditionGroup;
    
    for (const idx of groupPath) {
      conditionGroup = conditionGroup.groups[idx];
    }
    
    conditionGroup.logic = conditionGroup.logic === 'AND' ? 'OR' : 'AND';
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  // Remove nested group
  const removeNestedGroup = (lenderId, ruleIndex, groupIndex, groupPath = []) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    let conditionGroup = updatedLenders[lenderIndex].conditionalRules[ruleIndex].conditionGroup;
    
    for (const idx of groupPath) {
      conditionGroup = conditionGroup.groups[idx];
    }
    
    conditionGroup.groups.splice(groupIndex, 1);
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  // Add criterion to THEN/ELSE branch
  const addBranchCriterion = (lenderId, ruleIndex, branch) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    const rule = updatedLenders[lenderIndex].conditionalRules[ruleIndex];
    
    const criteriaArray = branch === 'then' ? rule.thenCriteria : rule.elseCriteria;
    criteriaArray.push({
      fieldId: '',
      fieldName: '',
      operator: '>=',
      value: '',
      weight: 5,
      required: false
    });
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  // Update criterion in THEN/ELSE branch
  const updateBranchCriterion = (lenderId, ruleIndex, branch, criterionIndex, field, value) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    const rule = updatedLenders[lenderIndex].conditionalRules[ruleIndex];
    const criteriaArray = branch === 'then' ? rule.thenCriteria : rule.elseCriteria;
    const criterion = criteriaArray[criterionIndex];
    
    if (field === 'fieldId') {
      const selectedField = availableFields.find(f => f.id === value);
      criterion.fieldId = value;
      criterion.fieldName = selectedField?.name || value;
      criterion.operator = '>=';
      criterion.value = '';
    } else {
      criterion[field] = value;
    }
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  // Remove criterion from THEN/ELSE branch
  const removeBranchCriterion = (lenderId, ruleIndex, branch, criterionIndex) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    if (lenderIndex === -1) return;
    
    const updatedLenders = [...lenders];
    const rule = updatedLenders[lenderIndex].conditionalRules[ruleIndex];
    const criteriaArray = branch === 'then' ? rule.thenCriteria : rule.elseCriteria;
    criteriaArray.splice(criterionIndex, 1);
    
    setLenders(updatedLenders);
    setCriteriaChanged(prev => ({ ...prev, [lenderId]: true }));
  };
  
  // ============================================================================
  // SAVE HANDLER
  // ============================================================================
  
  const saveLenderCriteria = async (lenderId) => {
    const lender = lenders.find(l => l.id === lenderId);
    if (!lender) return;
    
    try {
      setSaving(true);
      
      await updateAllLenderCriteria(lenderId, {
        simpleCriteria: lender.simpleCriteria.filter(c => c.fieldId),
        conditionalRules: lender.conditionalRules,
        hideUntilQualified: lender.hideUntilQualified
      });
      
      setCriteriaChanged(prev => ({ ...prev, [lenderId]: false }));
      success(`Criteria updated for ${lender.name}`);
    } catch (err) {
      console.error('Error saving criteria:', err);
      notifyError(`Failed to save criteria for ${lender.name}`);
    } finally {
      setSaving(false);
    }
  };
  
  // ============================================================================
  // COPY HANDLER
  // ============================================================================
  
  const handleCopyCriteria = async () => {
    if (!copySource || !copyTarget) return;
    
    try {
      setSaving(true);
      await copyCriteria(copySource, copyTarget, true, true);
      
      // Reload settings
      const settingsData = await getAdvancedCriteriaSettings();
      if (settingsData.lenders) {
        const processedLenders = settingsData.lenders.map(lender => ({
          id: lender.lenderId,
          name: lender.name,
          financingType: lender.financingType || 'working-capital',
          hideUntilQualified: lender.hideUntilQualified === true,
          simpleCriteria: lender.simpleCriteria || [],
          conditionalRules: lender.conditionalRules || []
        }));
        setLenders(processedLenders);
      }
      
      setShowCopyModal(false);
      setCopySource(null);
      setCopyTarget(null);
      success('Criteria copied successfully');
    } catch (err) {
      console.error('Error copying criteria:', err);
      notifyError('Failed to copy criteria');
    } finally {
      setSaving(false);
    }
  };
  
  // ============================================================================
  // VALUE INPUT RENDERER
  // ============================================================================
  
  const renderValueInput = (fieldId, value, onChange, operator) => {
    const field = availableFields.find(f => f.id === fieldId);
    if (!field) return <Form.Control type="text" value={value || ''} onChange={onChange} placeholder="Enter value" />;
    
    const type = field.type?.toLowerCase() || 'string';
    const mappedPicklistOptions = (field.picklistValues || []).map(option => ({
      value: typeof option === 'string' ? option : option.value,
      label: typeof option === 'string' ? option : option.label || option.value
    }));
    
    // Multi-select for is_one_of or is_not operators
    if (operator === 'is_one_of' || operator === 'is_not') {
      const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
      const selectedOptions = selectedValues.map(val => ({ value: val, label: val }));
      
      let options = [];
      if (fieldId === 'Industry') {
        options = dynamicIndustries.map(ind => ({ value: ind, label: ind }));
      } else if (isStateField(fieldId)) {
        options = stateCodes.map(state => ({ value: state, label: state }));
      } else if (mappedPicklistOptions.length > 0) {
        options = mappedPicklistOptions;
      } else {
        options = selectedOptions;
      }
      
      const SelectComponent = (
        fieldId === 'Industry' || isStateField(fieldId) || mappedPicklistOptions.length > 0
      ) ? Select : CreatableSelect;
      
      return (
        <SelectComponent
          isMulti
          options={options}
          value={selectedOptions}
          onChange={(selected) => {
            const newValues = selected ? selected.map(opt => opt.value) : [];
            onChange({ target: { value: newValues } });
          }}
          placeholder={`Select values to ${operator === 'is_not' ? 'exclude' : 'include'}`}
          styles={selectStyles}
          menuPortalTarget={document.body}
        />
      );
    }
    
    // Months dropdown for older_than_months
    if (isDateComparisonField(fieldId) && operator === 'older_than_months') {
      const monthOptions = Array.from({ length: 120 }, (_, i) => i + 1);
      return (
        <Form.Select value={value || ''} onChange={onChange}>
          <option value="">Select months</option>
          {monthOptions.map(months => (
            <option key={months} value={months}>
              {months} {months === 1 ? 'month' : 'months'}
            </option>
          ))}
        </Form.Select>
      );
    }
    
    // State dropdown
    if (isStateField(fieldId) && operator === '=') {
      return (
        <Form.Select value={value || ''} onChange={onChange}>
          <option value="">Select a state</option>
          {stateCodes.map(state => (
            <option key={state} value={state}>{state}</option>
          ))}
        </Form.Select>
      );
    }
    
    // Industry dropdown
    if (fieldId === 'Industry' && operator === '=') {
      return (
        <Form.Select value={value || ''} onChange={onChange}>
          <option value="">Select an industry</option>
          {dynamicIndustries.map(ind => (
            <option key={ind} value={ind}>{ind}</option>
          ))}
        </Form.Select>
      );
    }

    // Salesforce picklist dropdown, resolved through the saved field mapping
    if (mappedPicklistOptions.length > 0 && operator === '=') {
      return (
        <Form.Select value={value || ''} onChange={onChange}>
          <option value="">Select {field.name}</option>
          {mappedPicklistOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Form.Select>
      );
    }
    
    // Numeric input
    if (shouldUseNumericOperators(fieldId, type)) {
      return (
        <Form.Control 
          type="number" 
          value={value || ''} 
          onChange={onChange} 
          placeholder="Enter numeric value" 
        />
      );
    }
    
    // Date input
    if (type === 'date') {
      return <Form.Control type="date" value={value || ''} onChange={onChange} />;
    }
    
    // Boolean input
    if (type === 'boolean') {
      return (
        <Form.Select value={value || ''} onChange={onChange}>
          <option value="">Select</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </Form.Select>
      );
    }
    
    // Default text input
    return <Form.Control type="text" value={value || ''} onChange={onChange} placeholder="Enter value" />;
  };
  
  // ============================================================================
  // CONDITION GROUP RENDERER (RECURSIVE)
  // ============================================================================
  
  const renderConditionGroup = (lenderId, ruleIndex, conditionGroup, groupPath = [], depth = 0) => {
    const { logic, conditions = [], groups = [] } = conditionGroup;
    
    return (
      <div className={`condition-group depth-${depth}`}>
        <div className="condition-group-header d-flex align-items-center mb-2">
          <Button 
            variant={logic === 'AND' ? 'primary' : 'warning'} 
            size="sm"
            onClick={() => toggleGroupLogic(lenderId, ruleIndex, groupPath)}
            className="me-2"
          >
            {logic}
          </Button>
          <small className="text-muted">
            {logic === 'AND' ? 'All conditions must match' : 'Any condition must match'}
          </small>
          {depth > 0 && (
            <Button 
              variant="danger" 
              size="sm" 
              className="ms-auto"
              onClick={() => removeNestedGroup(lenderId, ruleIndex, groupPath[groupPath.length - 1], groupPath.slice(0, -1))}
            >
              <FaTrash />
            </Button>
          )}
        </div>
        
        {/* Render conditions */}
        {conditions.map((condition, condIndex) => (
          <div key={condIndex} className="condition-row d-flex align-items-center gap-2">
            <div style={{ flex: 2 }}>
              <Select
                options={fieldOptions}
                value={fieldOptions.flatMap(g => g.options).find(opt => opt.value === condition.fieldId)}
                onChange={(selected) => updateCondition(lenderId, ruleIndex, condIndex, 'fieldId', selected.value, groupPath)}
                placeholder="Select field"
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                options={condition.fieldId ? getFieldOperators(condition.fieldId) : []}
                value={getFieldOperators(condition.fieldId).find(opt => opt.value === condition.operator)}
                onChange={(selected) => updateCondition(lenderId, ruleIndex, condIndex, 'operator', selected.value, groupPath)}
                placeholder="Operator"
                isDisabled={!condition.fieldId}
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
            <div style={{ flex: 2 }}>
              {renderValueInput(
                condition.fieldId,
                condition.value,
                (e) => updateCondition(lenderId, ruleIndex, condIndex, 'value', e.target.value, groupPath),
                condition.operator
              )}
            </div>
            <Button 
              variant="danger" 
              size="sm"
              onClick={() => removeCondition(lenderId, ruleIndex, condIndex, groupPath)}
            >
              <FaTrash />
            </Button>
          </div>
        ))}
        
        {/* Render nested groups */}
        {groups.map((group, groupIndex) => (
          <div key={groupIndex} className="nested-group-container">
            {renderConditionGroup(lenderId, ruleIndex, group, [...groupPath, groupIndex], depth + 1)}
          </div>
        ))}
        
        {/* Add buttons */}
        <div className="condition-group-actions mt-2">
          <Button 
            variant="outline-primary" 
            size="sm" 
            className="me-2"
            onClick={() => addConditionToGroup(lenderId, ruleIndex, groupPath)}
          >
            <FaPlus className="me-1" /> Add Condition
          </Button>
          {depth < 2 && (
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => addNestedGroup(lenderId, ruleIndex, groupPath)}
            >
              <FaCodeBranch className="me-1" /> Add Nested Group
            </Button>
          )}
        </div>
      </div>
    );
  };
  
  // ============================================================================
  // BRANCH CRITERIA RENDERER
  // ============================================================================
  
  const renderBranchCriteria = (lenderId, ruleIndex, branch, criteria) => {
    return (
      <div className={`branch-criteria ${branch}-branch`}>
        <h6 className="text-uppercase fw-bold mb-2">
          {branch === 'then' ? '✓ THEN REQUIRE:' : '✗ ELSE REQUIRE:'}
        </h6>
        
        {criteria.map((criterion, critIndex) => (
          <div key={critIndex} className="criterion-card">
            <Row className="align-items-center">
              <Col md={3}>
                <Select
                  options={fieldOptions}
                  value={fieldOptions.flatMap(g => g.options).find(opt => opt.value === criterion.fieldId)}
                  onChange={(selected) => updateBranchCriterion(lenderId, ruleIndex, branch, critIndex, 'fieldId', selected.value)}
                  placeholder="Field"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                />
              </Col>
              <Col md={2}>
                <Select
                  options={criterion.fieldId ? getFieldOperators(criterion.fieldId) : []}
                  value={getFieldOperators(criterion.fieldId).find(opt => opt.value === criterion.operator)}
                  onChange={(selected) => updateBranchCriterion(lenderId, ruleIndex, branch, critIndex, 'operator', selected.value)}
                  placeholder="Op"
                  isDisabled={!criterion.fieldId}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                />
              </Col>
              <Col md={3}>
                {renderValueInput(
                  criterion.fieldId,
                  criterion.value,
                  (e) => updateBranchCriterion(lenderId, ruleIndex, branch, critIndex, 'value', e.target.value),
                  criterion.operator
                )}
              </Col>
              <Col md={2}>
                <Form.Range
                  min={1}
                  max={10}
                  value={criterion.weight || 5}
                  onChange={(e) => updateBranchCriterion(lenderId, ruleIndex, branch, critIndex, 'weight', Number(e.target.value))}
                />
                <small className="d-block text-center text-muted">Wt: {criterion.weight || 5}</small>
              </Col>
              <Col md={1}>
                <Form.Check
                  type="checkbox"
                  label="Req"
                  checked={criterion.required || false}
                  onChange={(e) => updateBranchCriterion(lenderId, ruleIndex, branch, critIndex, 'required', e.target.checked)}
                />
              </Col>
              <Col md={1}>
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={() => removeBranchCriterion(lenderId, ruleIndex, branch, critIndex)}
                >
                  <FaTrash />
                </Button>
              </Col>
            </Row>
          </div>
        ))}
        
        <Button 
          variant="outline-primary" 
          size="sm"
          onClick={() => addBranchCriterion(lenderId, ruleIndex, branch)}
        >
          <FaPlus className="me-1" /> Add {branch === 'then' ? 'THEN' : 'ELSE'} Criterion
        </Button>
      </div>
    );
  };
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading advanced criteria settings...</p>
      </Container>
    );
  }
  
  return (
    <Container className="my-5 advanced-criteria-page">
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-center mb-4">
            <Col>
              <h2 className="mb-0">
                <FaCodeBranch className="me-2" />
                Advanced Lender Criteria
              </h2>
              <p className="text-muted mt-1">
                Configure conditional criteria with IF-THEN-ELSE logic for intelligent lender matching
              </p>
            </Col>
            <Col xs="auto">
              <div className="d-flex align-items-center gap-3">
                <Button 
                  variant="outline-secondary" 
                  onClick={handleSyncLenders}
                  disabled={saving}
                >
                  <FaSync className="me-1" /> Sync Lenders
                </Button>
                <Button 
                  variant="outline-warning" 
                  onClick={handleFixLenderIds}
                  disabled={saving}
                  title="Fix lender IDs to match UI format (one-time migration)"
                >
                  🔧 Fix IDs
                </Button>
                <Button 
                  variant="outline-info" 
                  onClick={() => setShowCopyModal(true)}
                  disabled={saving}
                >
                  <FaCopy className="me-1" /> Copy Criteria
                </Button>
                <Form.Check
                  type="switch"
                  id="advanced-criteria-toggle"
                  label={enabled ? "Enabled" : "Disabled"}
                  checked={enabled}
                  onChange={handleToggleChange}
                  disabled={saving}
                  className="fs-5"
                />
              </div>
            </Col>
          </Row>
          
          {error && (
            <Alert variant="danger" className="mb-4">{error}</Alert>
          )}
          
          <Alert variant="info" className="mb-4">
            <Alert.Heading>How Advanced Criteria Works</Alert.Heading>
            <p>
              <strong>Simple Criteria:</strong> Always evaluated for every opportunity.
            </p>
            <p>
              <strong>Conditional Rules:</strong> Use IF-THEN-ELSE logic to apply different criteria based on opportunity characteristics.
              For example: "IF Industry is Construction THEN require 36 months time in business, ELSE require 12 months."
            </p>
            <p className="mb-0">
              <strong>AND/OR Logic:</strong> Combine conditions using AND (all must match) or OR (any must match) operators.
              You can nest groups for complex logic.
            </p>
          </Alert>
          
          {/* Financing Type Filter */}
          <div className="mb-3 d-flex align-items-center">
            <span className="me-3 fw-bold">Filter Lenders:</span>
            <ButtonGroup>
              <Button 
                variant={activeFilter === 'all' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveFilter('all')}
              >
                All
              </Button>
              <Button 
                variant={activeFilter === 'working-capital' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveFilter('working-capital')}
              >
                Working Capital
              </Button>
              <Button 
                variant={activeFilter === 'equipment-financing' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveFilter('equipment-financing')}
              >
                Equipment Financing
              </Button>
              <Button 
                variant={activeFilter === 'sba' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveFilter('sba')}
              >
                SBA
              </Button>
            </ButtonGroup>
          </div>
          
          {/* Lenders Accordion */}
          <Accordion className="mb-4">
            {lenders
              .filter(lender => activeFilter === 'all' || lender.financingType === activeFilter)
              .map((lender) => (
              <Accordion.Item key={lender.id} eventKey={lender.id}>
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100 pe-4">
                    <span>
                      {lender.name}
                      <Badge 
                        bg={
                          lender.financingType === 'equipment-financing' ? 'success' : 
                          lender.financingType === 'sba' ? 'warning' : 
                          'primary'
                        } 
                        className="ms-2" 
                        style={{ fontSize: '0.7rem' }}
                      >
                        {
                          lender.financingType === 'equipment-financing' ? 'Equipment' : 
                          lender.financingType === 'sba' ? 'SBA' : 
                          'Working Capital'
                        }
                      </Badge>
                    </span>
                    <div>
                      <Badge bg="secondary" className="me-2">
                        {lender.simpleCriteria.length} simple
                      </Badge>
                      <Badge bg="info">
                        {lender.conditionalRules.length} conditional
                      </Badge>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Form.Check
                    type="switch"
                    id={`hide-until-qualified-${lender.id}`}
                    className="mb-2"
                    label="Hide lender until required criteria are met"
                    checked={lender.hideUntilQualified}
                    onChange={event => {
                      const hideUntilQualified = event.target.checked;
                      setLenders(current => current.map(item => item.id === lender.id ? { ...item, hideUntilQualified } : item));
                      setCriteriaChanged(current => ({ ...current, [lender.id]: true }));
                    }}
                  />
                  <p className="text-muted small mb-4">While advanced criteria are enabled, hide this lender if required information is missing or a required rule fails. Submission overrides do not bypass this setting.</p>
                  {/* Simple Criteria Section */}
                  <div className="simple-criteria-section">
                    <h5 className="mb-3">Simple Criteria (Always Evaluated)</h5>
                    {lender.simpleCriteria.length === 0 ? (
                      <p className="text-muted text-center mb-3">No simple criteria defined yet.</p>
                    ) : (
                      lender.simpleCriteria.map((criterion, critIndex) => (
                        <div key={critIndex} className="criterion-card">
                          <Row className="align-items-center">
                            <Col md={3}>
                              <Select
                                options={fieldOptions}
                                value={fieldOptions.flatMap(g => g.options).find(opt => opt.value === criterion.fieldId)}
                                onChange={(selected) => updateSimpleCriterion(lender.id, critIndex, 'fieldId', selected.value)}
                                placeholder="Select field"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                              />
                            </Col>
                            <Col md={2}>
                              <Select
                                options={criterion.fieldId ? getFieldOperators(criterion.fieldId) : []}
                                value={getFieldOperators(criterion.fieldId).find(opt => opt.value === criterion.operator)}
                                onChange={(selected) => updateSimpleCriterion(lender.id, critIndex, 'operator', selected.value)}
                                placeholder="Operator"
                                isDisabled={!criterion.fieldId}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                              />
                            </Col>
                            <Col md={3}>
                              {renderValueInput(
                                criterion.fieldId,
                                criterion.value,
                                (e) => updateSimpleCriterion(lender.id, critIndex, 'value', e.target.value),
                                criterion.operator
                              )}
                            </Col>
                            <Col md={2}>
                              <Form.Range
                                min={1}
                                max={10}
                                value={criterion.weight || 5}
                                onChange={(e) => updateSimpleCriterion(lender.id, critIndex, 'weight', Number(e.target.value))}
                              />
                              <small className="d-block text-center text-muted">Wt: {criterion.weight || 5}</small>
                            </Col>
                            <Col md={1}>
                              <Form.Check
                                type="checkbox"
                                label="Req"
                                checked={criterion.required || false}
                                onChange={(e) => updateSimpleCriterion(lender.id, critIndex, 'required', e.target.checked)}
                              />
                            </Col>
                            <Col md={1}>
                              <Button 
                                variant="danger" 
                                size="sm"
                                onClick={() => removeSimpleCriterion(lender.id, critIndex)}
                              >
                                <FaTrash />
                              </Button>
                            </Col>
                          </Row>
                        </div>
                      ))
                    )}
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => addSimpleCriterion(lender.id)}
                      className="mt-2"
                    >
                      <FaPlus className="me-1" /> Add Simple Criterion
                    </Button>
                  </div>
                  
                  {/* Conditional Rules Section */}
                  <div className="mt-4">
                    <h5 className="mb-3">
                      <FaCodeBranch className="me-2 text-muted" />
                      Conditional Rules (IF-THEN-ELSE)
                    </h5>
                    {lender.conditionalRules.length === 0 ? (
                      <p className="text-muted text-center mb-3">No conditional rules defined yet.</p>
                    ) : (
                      lender.conditionalRules.map((rule, ruleIndex) => (
                        <div key={rule.id} className="conditional-rule-card">
                          <div className="card-header d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <Form.Check
                                type="switch"
                                checked={rule.enabled}
                                onChange={(e) => updateConditionalRule(lender.id, ruleIndex, { enabled: e.target.checked })}
                                className="me-2"
                              />
                              <Form.Control
                                type="text"
                                value={rule.name}
                                onChange={(e) => updateConditionalRule(lender.id, ruleIndex, { name: e.target.value })}
                                className="fw-bold border-0 bg-transparent"
                                style={{ width: 'auto' }}
                              />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Form.Check
                                type="checkbox"
                                label="Required"
                                checked={rule.required || false}
                                onChange={(e) => updateConditionalRule(lender.id, ruleIndex, { required: e.target.checked })}
                              />
                              <Button 
                                variant="danger" 
                                size="sm"
                                onClick={() => removeConditionalRule(lender.id, ruleIndex)}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </div>
                          <div className="card-body">
                            {/* IF Conditions */}
                            <div className="if-section mb-3">
                              <h6 className="text-uppercase fw-bold mb-2">IF:</h6>
                              {renderConditionGroup(lender.id, ruleIndex, rule.conditionGroup)}
                            </div>
                            
                            {/* THEN Branch */}
                            <div className="then-section mb-3">
                              {renderBranchCriteria(lender.id, ruleIndex, 'then', rule.thenCriteria)}
                            </div>
                            
                            {/* ELSE Branch */}
                            <div className="else-section">
                              {renderBranchCriteria(lender.id, ruleIndex, 'else', rule.elseCriteria)}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                    <Button 
                      variant="outline-primary"
                      onClick={() => addConditionalRule(lender.id)}
                      className="mt-2"
                    >
                      <FaPlus className="me-1" /> Add Conditional Rule
                    </Button>
                  </div>
                  
                  {/* Save Button */}
                  <div className="d-flex justify-content-end">
                    <Button
                      variant="primary"
                      onClick={() => saveLenderCriteria(lender.id)}
                      disabled={saving || !criteriaChanged[lender.id]}
                    >
                      {saving ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : 'Save Criteria'}
                    </Button>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card.Body>
      </Card>
      
      {/* Copy Modal */}
      <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Copy Criteria</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Copy From:</Form.Label>
            <Form.Select 
              value={copySource || ''} 
              onChange={(e) => setCopySource(e.target.value)}
            >
              <option value="">Select source lender</option>
              {lenders.map(lender => (
                <option key={lender.id} value={lender.id}>{lender.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Copy To:</Form.Label>
            <Form.Select 
              value={copyTarget || ''} 
              onChange={(e) => setCopyTarget(e.target.value)}
            >
              <option value="">Select target lender</option>
              {lenders.filter(l => l.id !== copySource).map(lender => (
                <option key={lender.id} value={lender.id}>{lender.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCopyModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCopyCriteria}
            disabled={!copySource || !copyTarget || saving}
          >
            {saving ? 'Copying...' : 'Copy Criteria'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LenderAdvancedCriteria;

